import React, { useMemo, useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Typography, Table, Divider, Empty, Tag, Alert, Progress, Spin } from 'antd';
import {
  BellOutlined,
  NotificationOutlined,
  ThunderboltOutlined,
  TeamOutlined,
  AppstoreOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { ApiService } from '../services/api';

const { Text, Title } = Typography;

// 卡片等高 + 副標固定一行，避免有無副標造成高度參差
const CARD_STYLE: React.CSSProperties = { width: '100%' };
const SUB_STYLE: React.CSSProperties = {
  fontSize: 12,
  display: 'block',
  marginTop: 4,
  minHeight: 18,
};

// 單一 alert 明細（展開列用）。status: 'active'=啟用；'inactive'=已刪除（soft delete）
interface AlertDetail {
  alert_id: number;
  name: string;
  status: string;
  language: string;
  channels: string; // "FB,IG,YT"
  keywords: string; // "台鐵 | 高鐵"
  daily_report: number; // 1 = 每日報告
  line_recipients: number; // LINE 收件人數（群組算成員數）
  line_users: number;
  line_groups: number;
  email_count: number;
  rss_count: number;
  updated_at: string | null;
}

// 後端 /api/trend-requests/alert-config 每列（一個客戶 service_id）
interface AlertConfigRow {
  service_id: string;
  org_name: string;
  total_alerts: number;
  active_alerts: number;
  daily_report_alerts: number;
  keyword_conditions: number;
  distinct_channels: number;
  channels_used: string; // e.g. "FB,IG,YT"
  alerts_with_line: number;
  line_recipients: number;
  last_config_update: string | null;
  alerts: AlertDetail[];
}

interface AlertAnalysisProps {
  data: {
    data?: Array<Record<string, any>>;
    time_range?: { start_date?: string; end_date?: string };
    eligible_services?: number; // 有 alert 額度的有效客戶數（分母）
    total_alert_quota?: number; // 這些客戶的 alert 額度總和
  } | null;
}

const AlertAnalysis: React.FC<AlertAnalysisProps> = ({ data }) => {
  const rows: AlertConfigRow[] = useMemo(
    () => (Array.isArray(data?.data) ? (data!.data as unknown as AlertConfigRow[]) : []),
    [data]
  );

  const startDate = data?.time_range?.start_date;
  const endDate = data?.time_range?.end_date;

  // 通知數（AlertRecord 千萬列，較慢）獨立載入：{alert_id: 命中次數}
  const [notifMap, setNotifMap] = useState<Record<string, number>>({});
  const [notifLoading, setNotifLoading] = useState(false);

  useEffect(() => {
    if (!startDate) return;
    let cancelled = false;
    setNotifLoading(true);
    setNotifMap({});
    ApiService.getTrendAlertNotifications({ start_date: startDate, end_date: endDate })
      .then((res) => {
        if (!cancelled) setNotifMap(res?.data || {});
      })
      .catch(() => {
        if (!cancelled) setNotifMap({});
      })
      .finally(() => {
        if (!cancelled) setNotifLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  // 每個客戶的期間通知數 = 其各 alert 命中數加總
  const notifByService = useMemo(() => {
    const acc: Record<string, number> = {};
    rows.forEach((r) => {
      acc[r.service_id] = r.alerts.reduce((s, a) => s + (notifMap[String(a.alert_id)] || 0), 0);
    });
    return acc;
  }, [rows, notifMap]);

  // 啟用中（未刪除）的 alert 才是有效 alert
  const activeAlerts = useMemo(
    () => rows.flatMap((r) => r.alerts).filter((a) => a.status === 'active'),
    [rows]
  );

  const totals = useMemo(() => {
    return {
      alerts: rows.reduce((s, r) => s + r.total_alerts, 0),
      active: activeAlerts.length,
      notifications: rows.reduce((s, r) => s + (notifByService[r.service_id] || 0), 0),
      // 有設定該通知方式的「啟用中 alert」數
      lineAlerts: activeAlerts.filter((a) => a.line_recipients > 0).length,
      emailAlerts: activeAlerts.filter((a) => a.email_count > 0).length,
    };
  }, [rows, activeAlerts, notifByService]);

  // 採用漏斗：分母＝有 alert 額度的有效客戶（含還沒設定的）
  const eligible = data?.eligible_services ?? 0;
  const funnel = useMemo(() => {
    const configured = rows.length;
    const notifiedAlerts = activeAlerts.filter(
      (a) => (notifMap[String(a.alert_id)] || 0) > 0
    ).length;
    return { eligible, configured, notifiedAlerts };
  }, [rows, activeAlerts, notifMap, eligible]);

  if (!rows.length) {
    return <Empty description="此區間沒有 Alert 設定資料" style={{ padding: '60px 0' }} />;
  }

  const pctOf = (n: number, d: number) => (d > 0 ? Math.round((n / d) * 100) : 0);

  // 展開列：該客戶的個別 alert（含設定關鍵字）
  const renderAlertDetails = (alerts: AlertDetail[]) => {
    const detailColumns: ColumnsType<AlertDetail> = [
      {
        title: '狀態',
        dataIndex: 'status',
        key: 'status',
        width: 90,
        render: (v: string) =>
          v === 'active' ? <Tag color="green">啟用</Tag> : <Tag color="red">已刪除</Tag>,
      },
      {
        title: '類型',
        dataIndex: 'daily_report',
        key: 'daily_report',
        width: 100,
        filters: [
          { text: '每日報告', value: 1 },
          { text: '即時通知', value: 0 },
        ],
        onFilter: (value, record) => (record.daily_report ? 1 : 0) === value,
        render: (v: number) =>
          v ? <Tag color="purple">每日報告</Tag> : <Tag color="cyan">即時</Tag>,
      },
      { title: 'Alert 名稱', dataIndex: 'name', key: 'name', width: 160 },
      {
        title: '設定關鍵字',
        dataIndex: 'keywords',
        key: 'keywords',
        render: (v: string) =>
          v ? <Text code copyable={{ text: v }}>{v}</Text> : <Text type="secondary">（無）</Text>,
      },
      {
        title: '監控頻道',
        dataIndex: 'channels',
        key: 'channels',
        width: 170,
        render: (v: string) =>
          v ? v.split(',').map((ch) => <Tag key={ch}>{ch}</Tag>) : '—',
      },
      {
        title: '通知方式 / 人數',
        key: 'notify_methods',
        width: 200,
        render: (_: unknown, a: AlertDetail) => {
          const tags = [];
          if (a.line_recipients > 0) {
            const detail = [
              a.line_users > 0 ? `個人${a.line_users}` : '',
              a.line_groups > 0 ? `群組${a.line_groups}` : '',
            ]
              .filter(Boolean)
              .join('/');
            tags.push(
              <Tag color="green" key="line">LINE {a.line_recipients}人{detail ? `（${detail}）` : ''}</Tag>
            );
          }
          if (a.email_count > 0) tags.push(<Tag color="blue" key="email">Email {a.email_count}</Tag>);
          return tags.length ? <span>{tags}</span> : <Text type="secondary">未設定</Text>;
        },
      },
      {
        title: '期間通知數',
        key: 'notifications',
        width: 110,
        sorter: (a, b) =>
          (notifMap[String(a.alert_id)] || 0) - (notifMap[String(b.alert_id)] || 0),
        render: (_: unknown, a: AlertDetail) =>
          (notifMap[String(a.alert_id)] || 0).toLocaleString(),
      },
    ];
    return (
      <Table<AlertDetail>
        dataSource={alerts}
        columns={detailColumns}
        rowKey="alert_id"
        size="small"
        pagination={false}
      />
    );
  };

  const columns: ColumnsType<AlertConfigRow> = [
    {
      title: '客戶',
      dataIndex: 'org_name',
      key: 'org_name',
      fixed: 'left',
      width: 240,
      render: (v: string, r) => (
        <span>
          {v}{' '}
          <Text type="secondary" style={{ fontSize: 12 }}>({r.service_id})</Text>
        </span>
      ),
    },
    {
      title: '啟用中 Alert',
      dataIndex: 'active_alerts',
      key: 'active_alerts',
      width: 110,
      sorter: (a, b) => a.active_alerts - b.active_alerts,
    },
    {
      title: '關鍵字條件',
      dataIndex: 'keyword_conditions',
      key: 'keyword_conditions',
      width: 100,
      sorter: (a, b) => a.keyword_conditions - b.keyword_conditions,
    },
    {
      title: '監控頻道',
      dataIndex: 'channels_used',
      key: 'channels_used',
      width: 220,
      render: (v: string) =>
        v
          ? v.split(',').map((ch) => (
              <Tag key={ch} style={{ marginBottom: 2 }}>{ch}</Tag>
            ))
          : '—',
    },
    {
      title: 'LINE 接收者',
      dataIndex: 'line_recipients',
      key: 'line_recipients',
      width: 100,
      sorter: (a, b) => a.line_recipients - b.line_recipients,
      render: (v: number) => (v > 0 ? v : <Text type="secondary">未綁</Text>),
    },
    {
      title: '期間通知(命中)',
      key: 'notifications',
      width: 130,
      sorter: (a, b) => (notifByService[a.service_id] || 0) - (notifByService[b.service_id] || 0),
      defaultSortOrder: 'descend',
      render: (_: unknown, r) => {
        const n = notifByService[r.service_id] || 0;
        return n > 0 ? n.toLocaleString() : <Text type="secondary">0</Text>;
      },
    },
    {
      title: '最後設定變更',
      dataIndex: 'last_config_update',
      key: 'last_config_update',
      width: 150,
      sorter: (a, b) => (a.last_config_update || '').localeCompare(b.last_config_update || ''),
      render: (v: string | null) => (v ? v.slice(0, 10) : '—'),
    },
  ];

  return (
    <>
      {data?.time_range && (
        <Text type="secondary">
          通知次數統計區間：{data.time_range.start_date} ~ {data.time_range.end_date || '今天'}
          （設定/條件為目前狀態，不受區間影響）
        </Text>
      )}
      <Divider />

      {/* 總覽（每張卡固定等高，副標行一律保留以對齊） */}
      <Row gutter={16} style={{ marginBottom: 24 }} align="stretch">
        <Col xs={12} md={5} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic
              title={<span><TeamOutlined /> 已設定客戶</span>}
              value={funnel.configured}
              suffix={`/ ${funnel.eligible}`}
            />
            <Text type="secondary" style={SUB_STYLE}>佔有額度客戶 {pctOf(funnel.configured, funnel.eligible)}%</Text>
          </Card>
        </Col>
        <Col xs={12} md={5} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic title={<span><BellOutlined /> 啟用中 Alert</span>} value={totals.active} />
            <Text type="secondary" style={SUB_STYLE}>不含已刪除</Text>
          </Card>
        </Col>
        <Col xs={12} md={5} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic
              title={<span><ThunderboltOutlined /> 期間通知(命中)</span>}
              value={notifLoading ? ' ' : totals.notifications}
              formatter={notifLoading ? () => <Spin size="small" /> : undefined}
            />
            <Text type="secondary" style={SUB_STYLE}>命中文章篇數</Text>
          </Card>
        </Col>
        <Col xs={12} md={5} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic
              title={<span><NotificationOutlined /> 有設 LINE 通知的 Alert</span>}
              value={totals.lineAlerts}
              suffix={`/ ${totals.active}`}
            />
            <Text type="secondary" style={SUB_STYLE}>佔啟用中 {pctOf(totals.lineAlerts, totals.active)}%</Text>
          </Card>
        </Col>
        <Col xs={12} md={4} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic
              title={<span><MailOutlined /> 有設 Email 通知的 Alert</span>}
              value={totals.emailAlerts}
              suffix={`/ ${totals.active}`}
            />
            <Text type="secondary" style={SUB_STYLE}>佔啟用中 {pctOf(totals.emailAlerts, totals.active)}%</Text>
          </Card>
        </Col>
      </Row>

      {/* 採用漏斗 */}
      <Card title={<span><AppstoreOutlined /> Alert 採用情況</span>} style={{ marginBottom: 24 }}>
        <Row gutter={16} align="stretch">
          <Col xs={24} md={8} style={{ display: 'flex' }}>
            <Card size="small" style={CARD_STYLE}>
              <Statistic title="可用 Alert 的客戶（有額度）" value={funnel.eligible} suffix="家" />
              <Progress percent={100} size="small" showInfo={false} />
              <Text type="secondary" style={SUB_STYLE}>分母：login.Service.alert_limit &gt; 0</Text>
            </Card>
          </Col>
          <Col xs={24} md={8} style={{ display: 'flex' }}>
            <Card size="small" style={CARD_STYLE}>
              <Statistic title="已設定 Alert 的客戶" value={funnel.configured} suffix="家" />
              <Progress percent={pctOf(funnel.configured, funnel.eligible)} size="small" showInfo={false} />
              <Text type="secondary" style={SUB_STYLE}>
                佔有額度客戶 {pctOf(funnel.configured, funnel.eligible)}%（{funnel.eligible - funnel.configured} 家未使用）
              </Text>
            </Card>
          </Col>
          <Col xs={24} md={8} style={{ display: 'flex' }}>
            <Card size="small" style={CARD_STYLE}>
              <Statistic
                title="期間實際被通知的 Alert"
                value={notifLoading ? ' ' : funnel.notifiedAlerts}
                suffix={notifLoading ? undefined : `/ ${totals.active}`}
                formatter={notifLoading ? () => <Spin size="small" /> : undefined}
              />
              <Progress
                percent={notifLoading ? 0 : pctOf(funnel.notifiedAlerts, totals.active)}
                size="small"
                showInfo={false}
              />
              <Text type="secondary" style={SUB_STYLE}>
                {notifLoading ? '統計中…' : `佔啟用中 Alert ${pctOf(funnel.notifiedAlerts, totals.active)}%`}
              </Text>
            </Card>
          </Col>
        </Row>
      </Card>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="「期間通知」= alert 命中的文章篇數（AlertRecord），非 LINE 實際送達則數"
        description="實際發送量還要乘上該 alert 的 LINE 接收對象數；LINE 送達成功/失敗僅存在 log、未落表。設定數與條件為目前狀態快照。"
      />

      {/* 每客戶明細（點展開看個別 alert 與關鍵字）
          等通知數載入完成才渲染，否則預設的「期間通知數 desc」排序會先以 0 排一次 */}
      <Card title={<Title level={5} style={{ margin: 0 }}>各客戶 Alert 設定與通知（點客戶列展開個別 alert）</Title>}>
        {notifLoading ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }} aria-busy="true">
            <Spin size="large" tip="統計期間通知數中…">
              <div style={{ padding: 24 }} />
            </Spin>
          </div>
        ) : (
          <Table<AlertConfigRow>
            dataSource={rows}
            columns={columns}
            rowKey="service_id"
            size="small"
            scroll={{ x: 1110 }}
            pagination={{ pageSize: 15, showSizeChanger: true }}
            expandable={{
              rowExpandable: (r) => r.alerts.length > 0,
              expandedRowRender: (r) => renderAlertDetails(r.alerts),
            }}
          />
        )}
      </Card>
    </>
  );
};

export default AlertAnalysis;
